import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { ShoppingBag, Heart, Truck, ShieldCheck, RotateCcw, Minus, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import ProductReviews from "@/components/ProductReviews";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const wishlist = useWishlist();
  const wished = wishlist?.isInWishlist(id);
  const [product, setProduct] = useState(null);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setColor((r.data.colors || [])[0] || null);
      setSize((r.data.sizes || [])[0] || null);
    }).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center font-display text-2xl">Loading…</div>;
  }

  const onAdd = async () => {
    if (!user) {
      toast.error("Please sign in to add items to cart");
      navigate("/login");
      return;
    }
    setAdding(true);
    try {
      await addItem(product.id, qty, color, size);
      toast.success(`${product.name} added to cart`);
    } catch (e) {
      toast.error("Could not add to cart");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-deepbrown/70 hover:text-rani mb-6" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden bg-white">
            <img src={product.images[activeImg] || product.images[0]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActiveImg(i)}
                  className={`h-20 w-20 rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-rani" : "border-transparent"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-marigold font-bold">{product.category.replace("-", " ")}</p>
          <h1 className="font-display font-bold text-deepbrown text-3xl sm:text-4xl mt-2 leading-tight">{product.name}</h1>
          <p className="font-display text-4xl text-rani font-bold mt-4">₹{product.price.toLocaleString("en-IN")}</p>

          <p className="mt-6 text-deepbrown/80 leading-relaxed">{product.description}</p>

          {product.colors?.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-widest font-bold text-deepbrown mb-2">Color</p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${color === c ? "border-rani bg-rani text-white" : "border-deepbrown/20 text-deepbrown hover:border-rani"}`}
                    data-testid={`color-${c.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-widest font-bold text-deepbrown mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${size === s ? "border-deepbrown bg-deepbrown text-ivory" : "border-deepbrown/20 text-deepbrown hover:border-deepbrown"}`}
                    data-testid={`size-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Stock status */}
          <div className="mt-6 flex items-center gap-3" data-testid="detail-stock-row">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest ${
              product.stock > 5
                ? "bg-emerald/10 text-emerald"
                : product.stock > 0
                ? "bg-marigold/15 text-marigold"
                : "bg-deepbrown/10 text-deepbrown/50"
            }`}>
              <span className={`h-2 w-2 rounded-full ${product.stock > 5 ? "bg-emerald" : product.stock > 0 ? "bg-marigold" : "bg-deepbrown/40"}`}></span>
              {product.stock > 5 ? "In stock" : product.stock > 0 ? `Only ${product.stock} left` : "Out of stock"}
            </span>
            <span className="text-sm text-deepbrown/60" data-testid="detail-stock-count">
              {product.stock > 0 ? `${product.stock} item${product.stock === 1 ? "" : "s"} available` : "Restocking soon"}
            </span>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center bg-white rounded-full border border-deepbrown/20">
              <button onClick={() => setQty(Math.max(1, qty - 1))} disabled={qty <= 1} className="h-12 w-12 grid place-items-center hover:text-rani disabled:opacity-30" data-testid="qty-decrease">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(product.stock || 1, qty + 1))} disabled={qty >= (product.stock || 0)} className="h-12 w-12 grid place-items-center hover:text-rani disabled:opacity-30" data-testid="qty-increase">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={onAdd}
              disabled={adding || product.stock <= 0}
              className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-rani text-white font-bold shadow-lg hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:hover:scale-100"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag className="h-5 w-5" />
              {product.stock <= 0 ? "Sold out" : "Add to Cart"}
            </button>
            <button
              onClick={async () => {
                if (!user) { toast.error("Sign in to use wishlist"); return; }
                await wishlist.toggle(product.id);
                toast.success(wished ? "Removed from wishlist" : "Added to wishlist");
              }}
              className={`h-14 w-14 rounded-full grid place-items-center border-2 transition ${wished ? "bg-rani text-white border-rani" : "bg-white text-rani border-rani/30 hover:border-rani"}`}
              data-testid="detail-wishlist-btn"
              aria-label="Toggle wishlist"
            >
              <Heart className={`h-5 w-5 ${wished ? "fill-white" : ""}`} />
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-sm">
            <div className="bg-marigold/15 rounded-xl p-3 text-center">
              <Truck className="h-5 w-5 text-deepbrown mx-auto mb-1" />
              <p className="text-xs text-deepbrown font-semibold">Free over ₹999</p>
            </div>
            <div className="bg-emerald/15 rounded-xl p-3 text-center">
              <ShieldCheck className="h-5 w-5 text-emerald mx-auto mb-1" />
              <p className="text-xs text-deepbrown font-semibold">Secure payment</p>
            </div>
            <div className="bg-rani/10 rounded-xl p-3 text-center">
              <RotateCcw className="h-5 w-5 text-rani mx-auto mb-1" />
              <p className="text-xs text-deepbrown font-semibold">7-day returns</p>
            </div>
          </div>
        </div>
      </div>

      <ProductReviews productId={product.id} />
    </div>
  );
}
